// @spec TSK-STR-022
// @spec TSK-STR-023
// @spec TSK-STR-024
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import {
  CreateScheduleDialog,
  DeleteScheduleDialog,
  EditScheduleDialog,
  PauseScheduleDialog,
  ResumeScheduleDialog,
  ScheduleInstancesDialog,
  SchedulesList,
} from '@/features/settings/task-schedules';
import type { RecurringTaskSchedule } from '@/features/settings/task-schedules';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/task-schedules')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.recurringTaskSchedules'),
  }),
  component: TaskSchedulesPage,
});

/**
 * Recurring task schedules management page.
 *
 * Shows list of recurring task schedules and allows admins to create, edit,
 * pause/resume, and delete schedules.
 */
function TaskSchedulesPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { isAdmin, isLoading: isLoadingRole } = useOrgRole();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<RecurringTaskSchedule | null>(null);
  const [deletingSchedule, setDeletingSchedule] = useState<RecurringTaskSchedule | null>(null);
  const [pausingSchedule, setPausingSchedule] = useState<RecurringTaskSchedule | null>(null);
  const [resumingSchedule, setResumingSchedule] = useState<RecurringTaskSchedule | null>(null);
  const [viewingInstancesSchedule, setViewingInstancesSchedule] =
    useState<RecurringTaskSchedule | null>(null);

  useEffect(() => {
    if (!isLoadingRole && !isAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [isLoadingRole, isAdmin, navigate]);

  if (!isLoadingRole && !isAdmin) {
    return null;
  }

  const orgId = (organization?.publicMetadata as { internalId?: string } | undefined)?.internalId;

  if (isLoadingRole || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl space-y-6">
          <header>
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-5 w-96 mt-2" />
          </header>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('taskSchedulesPage.title')}</h1>
          <p className="mt-1 text-muted-foreground">{t('taskSchedulesPage.description')}</p>
        </header>

        <SchedulesList
          onCreateClick={() => {
            setIsCreateDialogOpen(true);
          }}
          onEditClick={(schedule) => {
            setEditingSchedule(schedule);
          }}
          onPauseClick={(schedule) => {
            setPausingSchedule(schedule);
          }}
          onResumeClick={(schedule) => {
            setResumingSchedule(schedule);
          }}
          onDeleteClick={(schedule) => {
            setDeletingSchedule(schedule);
          }}
          onViewInstancesClick={(schedule) => {
            setViewingInstancesSchedule(schedule);
          }}
          isAdmin={isAdmin}
        />

        {/* Create Dialog */}
        <CreateScheduleDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />

        {/* Edit Dialog */}
        {editingSchedule && (
          <EditScheduleDialog
            schedule={editingSchedule}
            open={!!editingSchedule}
            onOpenChange={(open) => {
              if (!open) {
                setEditingSchedule(null);
              }
            }}
          />
        )}

        {/* Delete Dialog */}
        {deletingSchedule && (
          <DeleteScheduleDialog
            schedule={deletingSchedule}
            open={!!deletingSchedule}
            onOpenChange={(open) => {
              if (!open) {
                setDeletingSchedule(null);
              }
            }}
          />
        )}

        {/* Pause Dialog (TSK-STR-024) */}
        {pausingSchedule && (
          <PauseScheduleDialog
            schedule={pausingSchedule}
            open={!!pausingSchedule}
            onOpenChange={(open) => {
              if (!open) {
                setPausingSchedule(null);
              }
            }}
          />
        )}

        {/* Resume Dialog (TSK-STR-024) */}
        {resumingSchedule && (
          <ResumeScheduleDialog
            schedule={resumingSchedule}
            open={!!resumingSchedule}
            onOpenChange={(open) => {
              if (!open) {
                setResumingSchedule(null);
              }
            }}
          />
        )}

        {/* View Instances Dialog (TSK-STR-023) */}
        {viewingInstancesSchedule && (
          <ScheduleInstancesDialog
            schedule={viewingInstancesSchedule}
            open={!!viewingInstancesSchedule}
            onOpenChange={(open) => {
              if (!open) {
                setViewingInstancesSchedule(null);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
